import React, { useState, useMemo } from 'react';
import {
  Content,
  Header,
  Link,
  Page,
  Progress,
  Table,
  ErrorPanel,
  StatusOK,
  StatusPending,
  StatusError,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { scaffolderApiRef, ScaffolderTask } from '@backstage/plugin-scaffolder-react';
import useAsync from 'react-use/esm/useAsync';
import { Typography } from '@material-ui/core';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';

import { ActivityHistoryFilters } from './ActivityHistoryFilters';
import { useActivityHistoryFilters, normalizeSystem } from './hooks';

const TaskStatusColumn = ({ status }: { status: string }) => {
  switch (status) {
    case 'processing':
      return <StatusPending>{status}</StatusPending>;
    case 'completed':
      return <StatusOK>{status}</StatusOK>;
    case 'error':
    case 'failed':
    case 'cancelled':
    default:
      return <StatusError>{status}</StatusError>;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const getSystemFromTask = (task: ScaffolderTask): string => {
  const params = task.spec?.parameters as Record<string, unknown> | undefined;
  if (!params) return '-';
  return normalizeSystem(params.system ?? params.systemEntityRef);
};

const getFeatureName = (task: ScaffolderTask): string => {
  return task.spec?.templateInfo?.entity?.metadata?.title
    || task.spec?.templateInfo?.entity?.metadata?.name
    || '-';
};

export const ActivityHistoryPage = () => {
  const scaffolderApi = useApi(scaffolderApiRef);
  const { filters, updateFilter, resetFilters, applyFilters } = useActivityHistoryFilters();

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [ownerFilter] = useState<'owned' | 'all'>('all');

  const { value, loading, error } = useAsync(async () => {
    if (scaffolderApi.listTasks) {
      return scaffolderApi.listTasks({
        filterByOwnership: ownerFilter,
        limit: 1000, // Fetch more for client-side filtering
        offset: 0,
      });
    }
    console.warn('listTasks is not implemented in the scaffolderApi');
    return { tasks: [], totalTasks: 0 };
  }, [scaffolderApi, ownerFilter]);

  const filteredTasks = useMemo(() => {
    if (!value?.tasks) return [];
    return applyFilters(value.tasks);
  }, [value?.tasks, applyFilters]);

  // Paginate filtered tasks
  const paginatedTasks = useMemo(() => {
    const start = page * limit;
    return filteredTasks.slice(start, start + limit);
  }, [filteredTasks, page, limit]);

  if (loading) {
    return (
      <Page themeId="home">
        <Header title="Activity History" subtitle="History of activities performed on the portal" />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="home">
        <Header title="Activity History" subtitle="History of activities performed on the portal" />
        <Content>
          <ErrorPanel error={error} />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="home">
      <Header title="Activity History" subtitle="History of activities performed on the portal" />
      <Content>
        <ActivityHistoryFilters
          filters={filters}
          onFilterChange={updateFilter}
          onReset={resetFilters}
        />

        <Table<ScaffolderTask>
          onRowsPerPageChange={pageSize => {
            setPage(0);
            setLimit(pageSize);
          }}
          onPageChange={newPage => setPage(newPage)}
          options={{ pageSize: limit, emptyRowsWhenPaging: false, search: false }}
          data={paginatedTasks}
          page={page}
          totalCount={filteredTasks.length}
          title="Activities"
          columns={[
            {
              title: 'Task ID',
              field: 'id',
              render: row => (
                <Link to={`/create/tasks/${row.id}`}>{row.id}</Link>
              ),
            },
            {
              title: 'Activity',
              field: 'spec.templateInfo.entity.metadata.title',
              render: row => {
                const entityRef = row.spec?.templateInfo?.entityRef;
                if (entityRef) {
                  try {
                    parseEntityRef(entityRef);
                    return <EntityRefLink entityRef={entityRef} title={getFeatureName(row)} />;
                  } catch {
                    return <Typography variant="body2">{getFeatureName(row)}</Typography>;
                  }
                }
                return <Typography variant="body2">{getFeatureName(row)}</Typography>;
              },
            },
            {
              title: 'System',
              field: 'spec.parameters.system',
              render: row => (
                <Typography variant="body2">{getSystemFromTask(row)}</Typography>
              ),
            },
            {
              title: 'Created',
              field: 'createdAt',
              render: row => (
                <Typography variant="body2">{formatDate(row.createdAt)}</Typography>
              ),
              defaultSort: 'desc',
            },
            {
              title: 'User',
              field: 'spec.user.ref',
              render: row => {
                const userRef = row.spec?.user?.ref;
                if (userRef) {
                  try {
                    parseEntityRef(userRef);
                    return <EntityRefLink entityRef={userRef} />;
                  } catch {
                    return <Typography variant="body2">{userRef}</Typography>;
                  }
                }
                return <Typography variant="body2">-</Typography>;
              },
            },
            {
              title: 'Status',
              field: 'status',
              render: row => <TaskStatusColumn status={row.status} />,
            },
          ]}
        />
      </Content>
    </Page>
  );
};
